import React, { useEffect, useState } from "react";
import { Box, Dialog, DialogContent, DialogTitle, Slide, Typography, Grid, DialogActions, CircularProgress, Chip, Button } from '@mui/material';
import { TransitionProps } from "@mui/material/transitions";
import BlockIcon from '@mui/icons-material/Block';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MessageIcon from '@mui/icons-material/Message';
import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PinDropIcon from '@mui/icons-material/PinDrop';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import { AppColor, Fonts } from "../../../types/Constants";
import { QueuedReport } from "../../../types/models/QueuedReport";
import { UserProfile } from "../../../types/models/Beacon";
import { useAppDispatch } from "../../../utils/hooks";
import { getDateTimeString } from "../../../utils/utils";
import { BanUser, RemoveBeacon, dismissReport, getUserDetail } from "../../../redux/actions";
import { QueuedReportListColumns, ReportHistoryListColumns } from "../queuedReportList/QueuedReportListTypes";
import AppConfirmDialog from "../../../components/AppConfirmDialog";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  // eslint-disable-next-line no-undef
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface AppConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  report: QueuedReport;
  setRefreshFlag: (refresh: boolean) => void;
}

const QueuedReportDialog: React.FC<AppConfirmDialogProps> = ({ open, onClose, report, setRefreshFlag }) => {

  const QueuedReportActions = ["Dismiss", "Delete Beacon", "Ban User"] as const;
  const dispatch = useAppDispatch();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [confirmDialogAction, setConfirmDialogAction] = useState<(typeof QueuedReportActions)[number]>("Dismiss");
  const [reportedUserEmail, setReportedUserEmail] = useState<string>();
  const [loadingUserProfile, setLoadingUserProfile] = useState<boolean>(false);

  useEffect(() => {
    if (open && report.reported_user_id) {
      setLoadingUserProfile(true);
      setReportedUserEmail(undefined);
      dispatch(
        getUserDetail(report.reported_user_id, (success, data) => {
          if (success) {
            const profile: UserProfile = data;
            const email = profile.UserAttributes.find((x) => x.Name === "email")?.Value;
            setReportedUserEmail(email);
            setLoadingUserProfile(false);
          } else {
            setLoadingUserProfile(false);
          }
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const getConfirmMessage = () => {
    switch (confirmDialogAction) {
      case "Dismiss":
        return <Typography>Are you sure you want to <span style={{ color: "red" }}>dismiss</span> the report for the {report.report_type.toLowerCase()} &quot;{report.report_title}&quot;?</Typography>;
      case "Delete Beacon":
        return <Typography>Are you sure you want to <span style={{ color: "red" }}>delete</span> the beacon &quot;{report.report_title}&quot;?</Typography>;
      case "Ban User":
        return <Typography>Are you sure you want to <span style={{ color: "red" }}>ban</span> the user &quot;{report.report_title}&quot;?</Typography>;
      default:
        return "";
    }
  };

  const handleOnAction = () => {
    setIsSubmitting(true);
    setConfirmDialogOpen(false);
    switch (confirmDialogAction) {
      case "Dismiss":
        dispatch(dismissReport(
          report.id,
          (result) => {
            if (result) { setRefreshFlag(true); }
            setIsSubmitting(false);
          }));
        break;
      case "Delete Beacon":
        dispatch(RemoveBeacon(
          report.report_id ?? "",
          (result) => {
            if (result) { setRefreshFlag(true); }
            setIsSubmitting(false);
          },
          report.id));
        break;
      case "Ban User":
        dispatch(BanUser(
          report.reported_user_id ?? "",
          (result) => {
            if (result) { setRefreshFlag(true); }
            setIsSubmitting(false);
          },
          report.id));
        break;
    }
  };

  const typeIconElement = () => {
    switch (report.report_type) {
      case "Beacon":
        return <PinDropIcon sx={{ color: AppColor.baseOrange, mr: 1 }} />;
      case "User":
        return <PersonIcon sx={{ color: AppColor.baseOrange, mr: 1 }} />;
      case "Message":
        return <MessageIcon sx={{ color: AppColor.baseOrange, mr: 1 }} />;
      default:
        break;
    }
  };

  const actionTypeIconElement = () => {
    switch (report.actioned_type) {
      case "Dismiss":
        return <BlockIcon sx={{ color: AppColor.baseOrange, mr: 1 }} />;
      case "BanUser":
        return <PersonOffIcon sx={{ color: AppColor.baseOrange, mr: 1 }} />;
      case "DeleteBeacon":
        return <LocationOffIcon sx={{ color: AppColor.baseOrange, mr: 1 }} />;
      default:
        break;
    }
  };

  const userEmailElement = () => {
    if (loadingUserProfile) {
      return (
        <CircularProgress
          size="1.5rem"
          sx={{ ml: 3, color: AppColor.baseOrange }}
        />);
    }
    else if (reportedUserEmail) {
      return (
        <Chip
          icon={<EmailOutlined fontSize="small" />}
          sx={{ ml: 2, px: 1, height: "24px" }}
          clickable
          onClick={() => { window.open(`mailto:${reportedUserEmail}`, "_blank"); }}
          label={reportedUserEmail}
        />);
    } else {
      return <Box />;
    }
  };

  return (
    <Dialog
      TransitionComponent={Transition}
      open={open}
      onClose={() => onClose()}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: Fonts.SEMI_BOLD,
            }}
            id="alert-dialog-title"
          >
            Report for {report.report_type}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              onClose();
            }}>
            Close
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers={true} sx={{ "& .MuiGrid-item": { padding: 3, border: "0.2px solid silver" } }}>
        <Grid container sx={{ border: "0.2px solid silver" }}>
          <Grid item xs={3}>{QueuedReportListColumns.type}</Grid>
          <Grid item xs={9}>
            <Box sx={{ display: "flex" }}>
              {typeIconElement()}
              {report.report_type}
            </Box>
          </Grid>
          {report.report_type !== "User" &&
            <Grid item xs={3}>{report.report_type}</Grid>
          }
          {report.report_type !== "User" &&
            <Grid item xs={9}>
              {(report.report_title && report.report_id) ?
                report.report_title
                :
                (<Box>
                  <Box sx={{ color: "red" }}>Deleted</Box>
                  <Box sx={{ fontSize: 14 }}>Beacon Id:{report.target_id}</Box>
                </Box>)}
            </Grid>}
          <Grid item xs={3}>User</Grid>
          <Grid item xs={9}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {report.reported_user_name}
              {userEmailElement()}
            </Box>
          </Grid>


        </Grid>
        <Grid container sx={{ mt: 4, border: "0.2px solid silver" }}>
          <Grid item xs={3}>{QueuedReportListColumns.reason}</Grid>
          <Grid item xs={9}>{report.reason}</Grid>
          <Grid item xs={3}>{QueuedReportListColumns.created_at}</Grid>
          <Grid item xs={9}>{getDateTimeString(report.reported_at)}</Grid>
          <Grid item xs={3}>{QueuedReportListColumns.owner}</Grid>
          <Grid item xs={9}>{report.owner_name}</Grid>
        </Grid>
        {report.actioned_at &&
          <Box>
            <Typography sx={{ fontSize: 18, mt: 4, ml: 1 }}>Action</Typography>
            <Grid container sx={{ border: "0.2px solid silver" }}>
              <Grid item xs={3}>{ReportHistoryListColumns.action}</Grid>
              <Grid item xs={9}>
                <Box sx={{ display: "flex" }}>
                  {actionTypeIconElement()}
                  {report.actioned_type}
                </Box>
              </Grid>
              <Grid item xs={3}>{ReportHistoryListColumns.actioned_at}</Grid>
              <Grid item xs={9}>{getDateTimeString(report.actioned_at)}</Grid>
              <Grid item xs={3}>{ReportHistoryListColumns.actioned_by}</Grid>
              <Grid item xs={9}>{report.actioned_user_name}</Grid>
            </Grid>
          </Box>
        }
      </DialogContent>
      {
        !report.actioned_at &&
        <DialogActions>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Button
              sx={{ marginRight: 2 }}
              variant="contained"
              disabled={isSubmitting}
              onClick={() => {
                setConfirmDialogAction("Dismiss");
                setConfirmDialogOpen(true);
              }}>
              <BlockIcon sx={{ mr: 1 }} />
              Dismiss
            </Button>
            {report.report_type === "Beacon" && (
              <Button
                sx={{ marginRight: 2 }}
                variant="contained"
                disabled={isSubmitting}
                onClick={() => {
                  setConfirmDialogAction("Delete Beacon");
                  setConfirmDialogOpen(true);
                }}>
                <DeleteOutlineIcon sx={{ mr: 1 }} />
                Delete Beacon
              </Button>
            )}
            {/* Ban - Moderation - Requires MOD_ONLY*/}
            <Button
              sx={{ marginRight: 2 }}
              variant="contained"
              disabled={isSubmitting}
              onClick={() => {
                setConfirmDialogAction("Ban User");
                setConfirmDialogOpen(true);
              }}>
              <PersonOffIcon sx={{ mr: 1 }} />
              Ban User
            </Button>
          </Box>
        </DialogActions>
      }
      <AppConfirmDialog
        open={confirmDialogOpen}
        onConfirm={() => {
          handleOnAction();
        }}
        onDeny={() => {
          setConfirmDialogOpen(false);
        }}
        title={getConfirmMessage()}
        dialogTitle={confirmDialogAction}
        content=""
      />
      {isSubmitting && <Box sx={{
        position: "absolute",
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }}>
        <CircularProgress sx={{ color: AppColor.baseOrange }} />
      </Box>}
    </Dialog >

  );
};

export default QueuedReportDialog;
